import { Button, Classes, Intent, Spinner } from "@blueprintjs/core";
import classNames from "classnames";
import { Banner } from "components/common/Banner";
import { Surface } from "components/common/Surface";
import { Custom_Upgrade_Validation_Failure_Severities_Enum as FailureSeverity } from "generated/graphql";
import { ClusterModelSet } from "in_queue/types/clusterType";
import {
    ModelSetValidation,
    ModelSetValidationInfo,
    ValidationFailure,
    ValidationState
} from "in_queue/utils/customUpgradeValidation";
import { DateTime } from "luxon";
import ReactMarkdown from "react-markdown";
import css from "./ValidationBanner.module.scss";
import { VALIDATION_ICON, VALIDATION_INTENT } from "./validationStatusVisuals";

type BannerState = Exclude<ValidationState, "noModelSet">;

const FailureRow: React.FC<{ readonly failure: ValidationFailure }> = ({
    failure
}) => (
    <div className={css["ValidationBanner-failure"]}>
        <span className={css["ValidationBanner-failure-bullet"]} aria-hidden>
            ×
        </span>
        {failure.variantDescriptions.length > 0 && (
            <span className={css["ValidationBanner-failure-variant"]}>
                {failure.variantDescriptions.join(", ")}
            </span>
        )}
        <div className={css["ValidationBanner-failure-message"]}>
            <ReactMarkdown>{failure.message}</ReactMarkdown>
        </div>
    </div>
);

const FailureList: React.FC<{
    readonly failures: ReadonlyArray<ValidationFailure>;
}> = ({ failures }) =>
    failures.length === 0 ? null : (
        <Surface
            tone="white"
            bordered
            padding="small"
            className={css["ValidationBanner-failures"]}
        >
            {failures.map((failure, index) => (
                <FailureRow key={index} failure={failure} />
            ))}
        </Surface>
    );

const sourceFiles = (count: number, capitalized = false): string => {
    const noun = count === 1 ? "source file" : "source files";
    return capitalized ? `${noun[0].toUpperCase()}${noun.slice(1)}` : noun;
};

const BANNER_TEXT: Record<
    BannerState,
    { title: string; subtext: (fileCount: number) => string }
> = {
    usable: {
        title: "This upgrade is current",
        subtext: () => "Validated against the current model set."
    },
    stale: {
        title: "This upgrade is stale",
        subtext: () => `Model files have changed since the last validation.`
    },
    validating: {
        title: "Validating this upgrade…",
        subtext: (n) =>
            `Applying the ${sourceFiles(n)} to the current model set.`
    },
    failed: {
        title: "Validation failed",
        subtext: (n) =>
            `${
                n === 1 ? "The source file" : "One or more source files"
            } could not be validated against the current model set.`
    }
};

interface ValidationBannerProps {
    readonly info: ModelSetValidationInfo;
    readonly modelSet: ClusterModelSet | null;
    readonly modelSets: ReadonlyArray<ClusterModelSet>;
    readonly fileCount: number;
    /** Whether the current user may start a validation (admin, customer upgrade). */
    readonly canValidate: boolean;
    readonly onValidate: () => void;
    readonly validating: boolean;
}

const usableSubtext = (
    modelSet: ClusterModelSet | null,
    completedAt: Date | null
): string => {
    if (modelSet === null) {
        return BANNER_TEXT.usable.subtext(0);
    }
    const when =
        completedAt !== null
            ? ` · ${DateTime.fromJSDate(completedAt).toRelative()}`
            : "";
    return `Validated against ${modelSet.name}${when}.`;
};

const failedSubtext = (
    modelSet: ClusterModelSet | null,
    completedAt: Date | null
): string => {
    const target = modelSet?.name ?? "the current model set";
    const when =
        completedAt !== null
            ? ` · ${DateTime.fromJSDate(completedAt).toRelative()}`
            : "";
    return `Failed validation against ${target}${when}.`;
};

const staleSubtext = (
    mostRecent: ModelSetValidation | null,
    modelSets: ReadonlyArray<ClusterModelSet>
): string => {
    const validatedAt = mostRecent?.completedAt ?? mostRecent?.submittedAt;
    if (mostRecent === null || !validatedAt) {
        return BANNER_TEXT.stale.subtext(0);
    }
    const when = DateTime.fromJSDate(validatedAt).toRelative();
    const name = modelSets.find(
        (ms) => ms.clusterModelSetId === mostRecent.clusterModelSetId
    )?.name;
    return name
        ? `Model files have changed since it was validated against ${name} · ${when}.`
        : `Model files have changed since it was last validated ${when}.`;
};

export const ValidationBanner: React.FC<ValidationBannerProps> = ({
    info,
    modelSet,
    modelSets,
    fileCount,
    canValidate,
    onValidate,
    validating
}) => {
    const { status, current, mostRecent } = info;
    if (status.state === "noModelSet") return null;

    const text = BANNER_TEXT[status.state];
    const description = (() => {
        if (status.state === "failed") {
            const failures = current?.failures ?? [];
            const errorMessage = current?.errorMessage ?? status.errorMessage;
            return (
                <div className={css["ValidationBanner-detail"]}>
                    <span>
                        {failedSubtext(modelSet, current?.completedAt ?? null)}
                    </span>
                    {failures.length > 0 ? (
                        <FailureList failures={failures} />
                    ) : (
                        errorMessage && (
                            <Surface
                                tone="white"
                                bordered
                                padding="small"
                                className={classNames(
                                    css["ValidationBanner-error"],
                                    Classes.MONOSPACE_TEXT
                                )}
                            >
                                {errorMessage}
                            </Surface>
                        )
                    )}
                </div>
            );
        }
        if (status.state === "usable") {
            const warnings = (current?.failures ?? []).filter(
                (failure) => failure.severity === FailureSeverity.Warning
            );
            if (warnings.length === 0) {
                return usableSubtext(modelSet, current?.completedAt ?? null);
            }
            return (
                <div className={css["ValidationBanner-detail"]}>
                    <span>
                        {usableSubtext(modelSet, current?.completedAt ?? null)}
                    </span>
                    <FailureList failures={warnings} />
                </div>
            );
        }
        if (status.state === "stale") {
            return staleSubtext(mostRecent, modelSets);
        }
        return text.subtext(fileCount);
    })();

    const icon =
        status.state === "validating" ? (
            <Spinner size={20} intent={VALIDATION_INTENT[status.state]} />
        ) : (
            VALIDATION_ICON[status.state]
        );

    const action =
        status.state === "stale" && canValidate ? (
            <Button
                className={css["ValidationBanner-action"]}
                intent={Intent.WARNING}
                size="large"
                text="Validate"
                onClick={onValidate}
                loading={validating}
            />
        ) : undefined;

    return (
        <Banner
            title={text.title}
            icon={icon}
            description={description}
            action={action}
            intent={VALIDATION_INTENT[status.state]}
        />
    );
};
